import React, { useState, useEffect } from 'react';
import { Box, styled, Typography, IconButton, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUrlLinks, DiscoverLink, sendConversation, ConversationResponse } from '@services/discover';
import { getCurrentUserId } from '@utils/userUtils';

const DiscoverDetailsContainer = styled(Box)({
  backgroundColor: '#fdffff',
  borderRadius: '12px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
});

const Header = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(253, 255, 255, 0.2)',
  borderBottom: '1px solid #35d7e4',
  padding: '0px 64px 0px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '124px',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  [theme.breakpoints.down('md')]: {
    padding: '16px 24px 12px',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'flex-start',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px 16px 12px',
  },
}));

const HeaderLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const HeaderTitle = styled(Typography)({
  fontSize: '24px',
  fontWeight: 500,
  color: '#262626',
  letterSpacing: '-0.5px',
});


const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: '16px 48px 90px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    padding: '16px 24px 90px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px 16px 90px',
  },
}));

const LoadingContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  flexDirection: 'column',
  gap: '16px',
  color: '#6b7280',
});

const ErrorContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  flexDirection: 'column',
  gap: '16px',
  color: '#ef4444',
  padding: '24px',
  textAlign: 'center',
});

const IframeContainer = styled(Box)({
  flex: 1,
  position: 'relative',
  '& iframe': {
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: '0 0 12px 12px',
    // prevent external floating widgets from overlapping our fixed input
    pointerEvents: 'auto',
  },
  // clip any external page floating controls at viewport bottom margin
  overflow: 'hidden',
  paddingBottom: 0,
  '@media (max-width: 99999px)': {
    // no-op, placeholder for custom constraints if needed
  },
});

const QuestionInput = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: '16px',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '900px',
  maxWidth: 'calc(100vw - 96px)',
  backgroundColor: '#ffffff',
  border: '1px solid #99d4e1',
  borderRadius: '12px',
  boxShadow: '0px 6px 25px 0px #e0f9ff',
  padding: '8px 8px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  zIndex: 20,
  [theme.breakpoints.down('md')]: {
    width: 'auto',
    left: '24px',
    right: '24px',
    transform: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    left: '16px',
    right: '16px',
    padding: '12px',
  },
}));

const QuestionInputField = styled('input')({
  flex: 1,
  border: 'none',
  outline: 'none',
  fontSize: '14px',
  color: '#262626',
  backgroundColor: '#ffffff',
  borderRadius: '10px',
  
  padding: '10px 12px 10px 40px',
  position: 'relative',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none',
  borderBottom: 'none',
  '&::placeholder': {
    color: '#9ca3af',
  },
  '&:focus': {
    outline: 'none',
    border: 'none',
    borderBottom: 'none',
    //boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.06)',
  },
});

const SendButton = styled(IconButton)({
  backgroundColor: '#0a829D',
  color: '#ffffff',
  borderRadius: '8px',
  padding: '12px',
  minWidth: '48px',
  minHeight: '48px',
  '&:hover': {
    backgroundColor: '#086e86',
  },
});

const ConversationResults = styled(Box)({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '800px',
  maxHeight: '80vh',
  backgroundColor: '#ffffff',
  border: '1px solid #99d4e1',
  borderRadius: '12px',
  boxShadow: '0px 6px 25px 0px #e0f9ff',
  padding: '24px',
  zIndex: 30,
  overflowY: 'auto',
});

const ConversationHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
  paddingBottom: '12px',
  borderBottom: '1px solid #e5e7eb',
});

const CloseButton = styled(IconButton)({
  backgroundColor: '#f3f4f6',
  color: '#6b7280',
  '&:hover': {
    backgroundColor: '#e5e7eb',
  },
});

const SummaryText = styled(Typography)({
  fontSize: '14px',
  lineHeight: 1.6,
  color: '#374151',
  marginBottom: '16px',
  whiteSpace: 'pre-line',
});

const SourcesContainer = styled(Box)({
  marginTop: '16px',
});

const SourceItem = styled(Box)({
  padding: '12px',
  backgroundColor: '#f8fafc',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  marginBottom: '8px',
});

const SourceTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#1f2937',
  marginBottom: '4px',
});

const SourceDescription = styled(Typography)({
  fontSize: '12px',
  color: '#6b7280',
  marginBottom: '8px',
});

const SourceLink = styled('a')({
  fontSize: '12px',
  color: '#0a829D',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
});

const SearchIconWrapper = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  color: '#93c5fd',
  position: 'absolute',
  left: 28,
});

const FollowUpContainer = styled(Box)({
  marginTop: '16px',
});

const FollowUpTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#1f2937',
  marginBottom: '8px',
});

const FollowUpItem = styled(Box)({
  padding: '8px 12px',
  backgroundColor: '#f0f9ff',
  border: '1px solid #bae6fd',
  borderRadius: '6px',
  marginBottom: '6px',
  cursor: 'pointer',
  fontSize: '12px',
  color: '#0369a1',
  '&:hover': {
    backgroundColor: '#e0f2fe',
  },
});

export default function DiscoverDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const [, setDiscoverLinks] = useState<DiscoverLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [currentName, setCurrentName] = useState<string>('');
  const [question, setQuestion] = useState<string>('');
  const [conversationData, setConversationData] = useState<ConversationResponse | null>(null);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [conversationError, setConversationError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [iframeLoading, setIframeLoading] = useState<boolean>(true);

  // Get link info from location state or props
  const linkId = location.state?.linkId || location.pathname.split('/').pop();
  const linkUrl = location.state?.linkUrl;
  const linkName = location.state?.linkName;

  useEffect(() => {
    const fetchDiscoverLinks = async () => {
      try {
        setLoading(true);
        setError(null);
        const userId = getCurrentUserId();
        
        if (userId === "0") {
          setError('User not authenticated');
          return;
        }

        const response = await getUrlLinks({
          user_id: parseInt(userId),
          industry_type: 'healthcare'
        });
        setDiscoverLinks(response.links);
        
        // Find the specific link if linkId is provided
        if (linkId) {
          const link = response.links.find(l => l.id.toString() === linkId);
          if (link) {
            setCurrentUrl(link.url);
            setCurrentName(link.pretty_name);
          } else {
            setError('Link not found');
          }
        } else if (linkUrl) {
          setCurrentUrl(linkUrl);
          setCurrentName(linkName || 'External Link');
        }
      } catch (err: any) {
        console.error('Error fetching discover links:', err);
        const msg = typeof err?.message === 'string' && err.message
          ? err.message
          : 'Failed to load discover links';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchDiscoverLinks();
  }, [linkId, linkUrl, linkName]);

  // Clear error when URL or link changes
  useEffect(() => {
    setConversationError(null);
    // show loader when URL changes
    if (currentUrl) {
      setIframeLoading(true);
    }
  }, [location.pathname, currentUrl]);

  const handleQuestionSubmit = async () => {
    if (question.trim() && currentUrl) {
      try {
        setConversationLoading(true);
        setConversationError(null);
        
        const userId = getCurrentUserId();
        if (userId === "0") {
          setConversationError('User not authenticated');
          return;
        }

        const response = await sendConversation({
          user_id: parseInt(userId),
          industry_type: 'healthcare',
          query: question.trim(),
          url: currentUrl,
          conversation_id: conversationId || null
        });

        // Update conversation ID for future messages
        if (response.conversation_id) {
          setConversationId(response.conversation_id);
        }

        // Navigate to chat page with conversation data
        navigate('/dashboard/discover/chat', {
          state: {
            linkUrl: currentUrl,
            linkName: currentName,
            linkId,
            conversationData: response,
            conversationId: response.conversation_id
          }
        });
      } catch (error: any) {
        console.error('Error sending conversation:', error);
        const msg = typeof error?.message === 'string' && error.message
          ? error.message
          : 'Failed to get response. Please try again.';
        setConversationError(msg);
      } finally {
        setConversationLoading(false);
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleQuestionSubmit();
    }
  };

  const handleCloseConversation = () => {
    setConversationData(null);
    setConversationId(null);
  };

  if (loading) {
    return (
      <DiscoverDetailsContainer>
        <LoadingContainer>
          <CircularProgress size={40} />
          <Typography>Loading discover details...</Typography>
        </LoadingContainer>
      </DiscoverDetailsContainer>
    );
  }

  if (error) {
    return (
      <DiscoverDetailsContainer>
        <Header>
          <HeaderLeft>
            {/* <BackButton onClick={handleBackClick}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </BackButton> */}
            <HeaderTitle>Discover Details</HeaderTitle>
          </HeaderLeft>
        </Header>
        <ErrorContainer>
          <Typography variant="h6">{error}</Typography>
          <Typography>Please try again or go back to discover page.</Typography>
        </ErrorContainer>
      </DiscoverDetailsContainer>
    );
  }

  return (
    <DiscoverDetailsContainer>
      <Header>
        <HeaderLeft>
          {/* <BackButton onClick={handleBackClick}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </BackButton> */}
          <HeaderTitle>{currentName || 'Discover Details'}</HeaderTitle>
        </HeaderLeft>
      </Header>

      <ContentArea>
        {/* <UrlBar>
          <Typography variant="caption">Loading:</Typography>
          <UrlText>{currentUrl}</UrlText>
        </UrlBar> */}
        
        <IframeContainer>
          {currentUrl && (
            <>
              {iframeLoading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff' }}>
                  <CircularProgress sx={{ color: '#0a829d' }} />
                </Box>
              )}
              <iframe
                src={currentUrl}
                title={currentName}
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
                loading="lazy"
                onLoad={() => setIframeLoading(false)}
              />
            </>
          )}
        </IframeContainer>
      </ContentArea>

      <QuestionInput>
        {/* <Divider /> */}
        <SearchIconWrapper>
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.75 15C12.2018 15 15 12.2018 15 8.75C15 5.29822 12.2018 2.5 8.75 2.5C5.29822 2.5 2.5 5.29822 2.5 8.75C2.5 12.2018 5.29822 15 8.75 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </SearchIconWrapper>
        <QuestionInputField
          type="text"
          placeholder="Ask a question about this site..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <SendButton onClick={handleQuestionSubmit} disabled={conversationLoading}>
          {conversationLoading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M3.69362 10.0002L0.299335 1.27199C0.0630953 0.664515 0.655438 0.0835794 1.2414 0.290074L1.33375 0.329333L19.3337 9.32933C19.852 9.58845 19.8844 10.298 19.4309 10.6134L19.3337 10.671L1.33375 19.671C0.750768 19.9625 0.117464 19.4265 0.268798 18.8239L0.299335 18.7283L3.69362 10.0002L0.299335 1.27199L3.69362 10.0002ZM2.4021 2.54056L5.01109 9.24956L11.6387 9.25015C12.0184 9.25015 12.3322 9.53231 12.3818 9.89838L12.3887 10.0002C12.3887 10.3798 12.1065 10.6936 11.7404 10.7433L11.6387 10.7502L5.01109 10.7496L2.4021 17.4597L17.3213 10.0002L2.4021 2.54056Z" fill="white"/>
            </svg>
          )}
        </SendButton>
      </QuestionInput>

      {/* Conversation Results Modal */}
      {conversationData && (
        <ConversationResults>
          <ConversationHeader>
            <Typography variant="h6">AI Response</Typography>
             <CloseButton onClick={handleCloseConversation}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </CloseButton>
          </ConversationHeader>
          
          <SummaryText>{conversationData.summary}</SummaryText>
          
          {conversationData.sources && conversationData.sources.length > 0 && (
            <SourcesContainer>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: '8px' }}>
                Sources:
              </Typography>
              {conversationData.sources.map((source, index) => (
                <SourceItem key={index}>
                  <SourceTitle>{source.title}</SourceTitle>
                  <SourceDescription>{source.description}</SourceDescription>
                  <SourceLink href={source.url} target="_blank" rel="noopener noreferrer">
                    {source.url}
                  </SourceLink>
                </SourceItem>
              ))}
            </SourcesContainer>
          )}
          
          {conversationData.suggested_follow_ups && conversationData.suggested_follow_ups.length > 0 && (
            <FollowUpContainer>
              <FollowUpTitle>Suggested Follow-ups:</FollowUpTitle>
              {conversationData.suggested_follow_ups.map((followUp, index) => (
                <FollowUpItem 
                  key={index}
                  onClick={() => {
                    setQuestion(followUp);
                    setConversationData(null);
                  }}
                >
                  {followUp}
                </FollowUpItem>
              ))}
            </FollowUpContainer>
          )}
        </ConversationResults>
      )}

      {/* Conversation Error */}
      {conversationError && (
        <Box
          sx={{
            position: 'fixed',
            bottom: '100px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 16px',
            color: '#dc2626',
            fontSize: '14px',
            fontFamily: '"Rethink Sans"',
            zIndex: 25,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span>{conversationError}</span>
          <IconButton
            size="small"
            onClick={() => setConversationError(null)}
            sx={{
              marginLeft: '8px',
              backgroundColor: '#fee2e2',
              color: '#b91c1c',
              '&:hover': { backgroundColor: '#fecaca' },
              width: 28,
              height: 28,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </IconButton>
        </Box>
      )}
    </DiscoverDetailsContainer>
  );
}
